import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, Users, Package, User } from "lucide-react";
import axios from "axios";

import { UserType, User as DefaultUser, getCookieValue } from "../data";
import { UserModal, BirthdayModal } from "../components/mid-order";
import StatCard from "../components/atoms/StatCard";
import DashboardHeader from "../components/molecules/DashboardHeader";
import SettingSection from "../components/molecules/SettingSection";
import OrdersOverviewChart from "../components/organisms/OrdersOverviewChart";
import OrderStatusChart from "../components/organisms/OrderStatusChart";

type Counts = { orders: number; customers: number; products: number };

export default function Profile() {
  const [user, setUser] = useState<UserType>(DefaultUser);
  const [counts, setCounts] = useState<Counts>({
    orders: 0,
    customers: 0,
    products: 0,
  });
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const userId = getCookieValue("user_id");
  const jwtToken = getCookieValue("jwt_token");
  const config = { headers: { Authorization: `Bearer ${jwtToken}` } };

  useEffect(() => {
    if (!userId || !jwtToken) {
      setErrorMessage("was not able to locate the user");
      return;
    }

    axios
      .get(`/api/v1/user/${userId}`, config)
      .then((res) => setUser({ ...res.data.data }))
      .catch(() => setErrorMessage("was not able to locate the user"));

    Promise.all([
      axios.get("/api/v1/orders", config),
      axios.get("/api/v1/customers", config),
      axios.get("/api/v1/products", config),
    ])
      .then(([ordersRes, customersRes, productsRes]) => {
        setCounts({
          orders: (ordersRes.data.data ?? []).length,
          customers: (customersRes.data.data ?? []).length,
          products: (productsRes.data.data ?? []).length,
        });
      })
      .catch(() => {});
  }, []);

  if (errorMessage) {
    return <div className="alert alert-danger">{errorMessage}</div>;
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <BirthdayModal />

      <DashboardHeader title={`Welcome back, ${user.firstName || "Admin"}!`} />

      <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8">
        {/* Stat cards */}
        <motion.div
          className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <StatCard
            name="Total Orders"
            icon={ShoppingCart}
            value={counts.orders}
            color="#6366F1"
          />
          <StatCard
            name="Total Customers"
            icon={Users}
            value={counts.customers}
            color="#8B5CF6"
          />
          <StatCard
            name="Total Products"
            icon={Package}
            value={counts.products}
            color="#EC4899"
          />
        </motion.div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <OrdersOverviewChart jwtToken={jwtToken} />
          <OrderStatusChart jwtToken={jwtToken} />
        </div>

        {/* Profile card */}
        <SettingSection icon={User} title="My Profile">
          <div className="flex flex-col sm:flex-row items-center mb-6">
            <img
              src={user.userPicPath || "/images/default-avatar.png"}
              alt={`${user.firstName} ${user.lastName}`}
              className="rounded-full w-20 h-20 object-cover mr-4 mb-4 sm:mb-0"
            />
            <div>
              <h3 className="text-lg font-semibold text-gray-100">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-gray-400">@{user.username}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="change_password.php"
              className="bg-gray-700 hover:bg-gray-600 text-gray-100 font-semibold py-2 px-4 rounded transition duration-200 text-center"
            >
              Change Password
            </a>
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded transition duration-200"
            >
              Edit Profile
            </button>
          </div>
        </SettingSection>
      </main>

      <UserModal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        user={user}
        config={config}
        setUser={setUser}
      />
    </div>
  );
}
